import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, ExternalLink } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function ModeBadge({ mode }) {
  if (mode === "live") return <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold" data-testid="order-mode-live">Live · real money</span>;
  if (mode === "test") return <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold" data-testid="order-mode-test">Test order</span>;
  return <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">Unknown</span>;
}

export default function AdminOrderDrawer({ orderId, onClose, onOpenFarm }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    setD(null);
    api.get(`/admin/orders/${orderId}`).then(r => setD(r.data)).catch(() => setD(false));
  }, [orderId]);
  if (!orderId) return null;
  const o = d?.order;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="admin-order-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[var(--ff-border)] p-4 flex items-center justify-between z-10">
          <p className="font-display text-xl">Order detail</p>
          <button onClick={onClose} data-testid="admin-order-drawer-close" className="p-1 hover:bg-stone-100 rounded"><X size={18} /></button>
        </div>

        {d === null && <p className="p-8 text-sm text-[var(--ff-text-muted)]">Loading…</p>}
        {d === false && <p className="p-8 text-sm text-rose-700">Failed to load order.</p>}
        {o && (
          <div className="p-5 space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-xs text-[var(--ff-text-muted)]">#{o.id}</p>
                <p className="font-display text-2xl mt-1">{money(o.total)}</p>
                <p className="text-xs text-[var(--ff-text-muted)]">{new Date(o.created_at).toLocaleString()}</p>
              </div>
              <div className="text-right space-y-1">
                <ModeBadge mode={o.payment_mode} />
                <p className="text-xs uppercase tracking-wider text-[var(--ff-primary)]">{o.marketplace_status || o.status}</p>
              </div>
            </div>

            <div className="card-flat p-4" data-testid="admin-order-customer">
              <p className="overline mb-1">Customer</p>
              <p className="text-sm font-medium">{d.customer?.name || "—"}</p>
              <p className="text-xs text-[var(--ff-text-muted)]">{d.customer?.email || "—"}{d.customer?.phone ? ` · ${d.customer.phone}` : ""}</p>
              {o.shipping_address && <p className="text-xs text-[var(--ff-text-muted)] mt-2">Ship to: {o.shipping_address}</p>}
            </div>

            <div>
              <p className="overline mb-2">Items</p>
              <div className="card-flat divide-y divide-[var(--ff-border)]">
                {(o.items || []).map((i, idx) => (
                  <div key={idx} className="p-3 flex justify-between text-sm">
                    <span>{i.name} × {i.qty}</span>
                    <span className="font-medium">{money((i.price || 0) * (i.qty || 1))}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-sm text-[var(--ff-text-muted)] space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span>{money(o.delivery_fee)}</span></div>
                <div className="flex justify-between"><span>Tax</span><span>{money(o.tax)}</span></div>
              </div>
            </div>

            {(d.suborders || []).length > 0 && (
              <div>
                <p className="overline mb-2">Rancher suborders ({d.suborders.length})</p>
                <div className="space-y-2" data-testid="admin-order-suborders">
                  {d.suborders.map(s => (
                    <div key={s.id} className="card-flat p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{s.vendor_name || s.vendor_id}</p>
                        <p className="text-xs text-[var(--ff-text-muted)]">{money(s.subtotal)} · {s.status} · payout {s.payout_status || "pending"} ({money(s.payout)})</p>
                      </div>
                      {onOpenFarm && (
                        <button onClick={() => onOpenFarm(s.vendor_id)} className="btn-secondary text-xs shrink-0" data-testid={`order-open-farm-${s.vendor_id}`}>
                          <ExternalLink size={12} /> Farm backend
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {o.session_id && <p className="text-[11px] text-[var(--ff-text-muted)] font-mono break-all">Stripe session: {o.session_id}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
